import { useEffect, useRef, useState } from "react";

import { Button, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { StarRatingMenu } from "components/StarRatingMenu";
import { ClientAppEvent, CompanyReviewSource } from "constants/index";
import { useWriterComponentState } from "hooks";

interface InlineCompanyReviewPromptProps {
  carrierCompanyId: number;
  carrierCompanyName: string;
  shipmentId: number;
}

export function InlineCompanyReviewPrompt(
  props: InlineCompanyReviewPromptProps,
) {
  const { carrierCompanyId, carrierCompanyName, shipmentId } = props;
  const posthog = usePostHog();
  const [rating, setRating] = useState<CompanyRatingValue | null>(null);
  const [reviewText, setReviewText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const hasTrackedShown = useRef(false);
  const hasTrackedClicked = useRef(false);

  useEffect(() => {
    if (!hasTrackedShown.current) {
      hasTrackedShown.current = true;
      posthog?.capture(ClientAppEvent.COMPANY_REVIEW_PROMPT_SHOWN, {
        carrier_company_id: carrierCompanyId,
        shipment_id: shipmentId,
        source: CompanyReviewSource.SHIPMENT_DETAIL_MICRO_REVIEW,
      });
    }
  }, [posthog, carrierCompanyId, shipmentId]);

  const { loading, write, error } = useWriterComponentState({
    errorMessage: "Failed to submit company review. Please try again.",
    writeConfig: {
      method: "post",
      url: `/companies/${carrierCompanyId}/reviews`,
      body: {
        rating,
        text: reviewText,
        source: CompanyReviewSource.SHIPMENT_DETAIL_MICRO_REVIEW,
      },
    },
    errorMessageKey: "message",
  });

  const handleRatingChange = (val: CompanyRatingValue | null) => {
    setRating(val);
    if (!hasTrackedClicked.current && val !== null) {
      hasTrackedClicked.current = true;
      posthog?.capture(ClientAppEvent.COMPANY_REVIEW_PROMPT_CLICKED, {
        carrier_company_id: carrierCompanyId,
        shipment_id: shipmentId,
        source: CompanyReviewSource.SHIPMENT_DETAIL_MICRO_REVIEW,
      });
    }
  };

  const onSubmit = () => {
    if (rating == null) return;
    write(() => {
      posthog?.capture(ClientAppEvent.COMPANY_REVIEW_SUBMITTED, {
        carrier_company_id: carrierCompanyId,
        shipment_id: shipmentId,
        rating,
        has_text_review: reviewText.length > 0,
        source: CompanyReviewSource.SHIPMENT_DETAIL_MICRO_REVIEW,
      });
      setSubmitted(true);
    });
  };

  if (submitted) {
    return (
      <Typography color="green">
        Your response has been recorded. Thank you.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      <Typography variant="body2" fontWeight="bold">
        {`How would you rate your overall experience with ${carrierCompanyName}?`}
      </Typography>
      <StarRatingMenu onStarRatingSet={handleRatingChange} rating={rating} />
      <TextField
        fullWidth
        minRows={3}
        multiline
        label="Detail your experience with the company. (optional)"
        value={reviewText}
        onChange={(e) => setReviewText(e.target.value)}
      />
      <LoadingWrappedNode loading={loading}>
        <Tooltip title="Submit your company review for this carrier">
          <span>
            <Button
              variant="contained"
              disabled={rating == null}
              onClick={onSubmit}
              sx={{ alignSelf: "flex-start" }}
            >
              Submit Review
            </Button>
          </span>
        </Tooltip>
        <Typography variant="body2" color="text.secondary">
          Company reviews are public. Your name and company name will be
          visible.
        </Typography>
      </LoadingWrappedNode>
      <ErrorTypography error={error} />
    </Stack>
  );
}
